import { Lead } from '@/hooks/useLeads';

export interface DecisionScore {
  score: number;
  reasons: string[];
  recommendedAction: 'whatsapp' | 'call' | 'email';
  urgency: 'high' | 'medium' | 'low';
  revenueAtRisk: number;
}

// Decision Score™ weights - revenue-focused prioritization
const TEMPERATURE_WEIGHTS = { hot: 30, warm: 15, cold: 5 };
const STAGE_WEIGHTS = { proposal: 25, appointment: 18, contacted: 10, new: 5 };

export function calculateDecisionScore(lead: Lead, dealValue: number = 0): DecisionScore {
  let score = 0;
  const reasons: string[] = [];
  const now = new Date();
  let revenueAtRisk = dealValue;

  // 1. Temperature weight (0-30 points)
  const tempScore = TEMPERATURE_WEIGHTS[lead.temperature as keyof typeof TEMPERATURE_WEIGHTS] || 5;
  score += tempScore;
  if (lead.temperature === 'hot') {
    reasons.push('Hot lead - high buying intent');
  }

  // 2. Pipeline stage weight (0-25 points)
  const stageScore = STAGE_WEIGHTS[lead.pipeline_stage as keyof typeof STAGE_WEIGHTS] || 0;
  score += stageScore;
  if (lead.pipeline_stage === 'proposal') {
    reasons.push('Proposal stage - ready to close');
  } else if (lead.pipeline_stage === 'appointment') {
    reasons.push('Appointment set - maintain momentum');
  }

  // 3. Overdue follow-up (0-25 points) - critical for urgency
  if (lead.next_action_at) {
    const nextAction = new Date(lead.next_action_at);
    const hoursOverdue = (now.getTime() - nextAction.getTime()) / (1000 * 60 * 60);
    
    if (hoursOverdue > 0) {
      const overdueDays = Math.floor(hoursOverdue / 24);
      if (overdueDays >= 3) {
        score += 25;
        reasons.push(`${overdueDays} days overdue - urgent`);
        revenueAtRisk *= 1.5;
      } else if (overdueDays >= 1) {
        score += 18;
        reasons.push(`${overdueDays} day(s) overdue`);
      } else {
        score += 12;
        reasons.push('Follow-up overdue today');
      }
    } else if (hoursOverdue > -24) {
      score += 8;
      reasons.push('Due today');
    }
  }

  // 4. Deal value boost (0-15 points)
  if (dealValue > 0) {
    if (dealValue >= 10000) {
      score += 15;
      reasons.push(`High-value opportunity: $${(dealValue/1000).toFixed(0)}K`);
    } else if (dealValue >= 5000) {
      score += 10;
      reasons.push(`$${(dealValue/1000).toFixed(0)}K deal value`);
    } else if (dealValue >= 1000) {
      score += 5;
    }
  }

  // 5. Staleness penalty (0-10 points)
  if (lead.last_interaction_at) {
    const daysSinceContact = (now.getTime() - new Date(lead.last_interaction_at).getTime()) / (1000 * 60 * 60 * 24);
    
    if (daysSinceContact > 7 && lead.temperature === 'hot') {
      score += 10;
      reasons.push('Hot lead inactive 7+ days');
      revenueAtRisk *= 1.3;
    } else if (daysSinceContact > 14) {
      score += 5;
      reasons.push('No contact in 2+ weeks');
    }
  } else if (lead.temperature !== 'cold') {
    score += 8;
    reasons.push('Never contacted');
  }

  // Cap at 100
  score = Math.min(100, Math.max(0, score));

  // Determine urgency based on score thresholds
  let urgency: 'high' | 'medium' | 'low' = 'low';
  if (score >= 70 || (dealValue >= 5000 && score >= 50)) {
    urgency = 'high';
  } else if (score >= 45) {
    urgency = 'medium';
  }

  // Determine recommended action
  let recommendedAction: 'whatsapp' | 'call' | 'email' = 'whatsapp';
  if (lead.temperature === 'hot' || lead.pipeline_stage === 'proposal' || dealValue >= 5000) {
    recommendedAction = 'call';
  } else if (!lead.phone && lead.email) {
    recommendedAction = 'email';
  }

  return { score, reasons, recommendedAction, urgency, revenueAtRisk };
}

export interface PriorityAction {
  id: string;
  leadId: string;
  lead: Lead;
  score: number;
  reasons: string[];
  recommendedAction: 'whatsapp' | 'call' | 'email';
  urgency: 'high' | 'medium' | 'low';
  title: string;
  suggestedMessage?: string;
  revenueAtRisk: number;
  dealValue: number;
}

interface Deal {
  id: string;
  lead_id: string;
  value: number | null;
  stage: string | null;
}

export function generatePriorityActions(leads: Lead[], deals: Deal[] = [], limit = 7): PriorityAction[] {
  // Filter out won/lost leads
  const activeLeads = leads.filter(
    lead => lead.pipeline_stage !== 'won' && lead.pipeline_stage !== 'lost'
  );

  // Build lead -> deal value map
  const leadDealValues = new Map<string, number>();
  deals.forEach(deal => {
    if (deal.stage !== 'won' && deal.stage !== 'lost' && deal.value) {
      const current = leadDealValues.get(deal.lead_id) || 0;
      leadDealValues.set(deal.lead_id, current + deal.value);
    }
  });

  // Calculate scores and sort
  const scoredLeads = activeLeads.map(lead => {
    const dealValue = leadDealValues.get(lead.id) || 0;
    const { score, reasons, recommendedAction, urgency, revenueAtRisk } = calculateDecisionScore(lead, dealValue);
    
    // Generate action-oriented title
    let title = `Contact ${lead.name}`;
    if (lead.next_action_at && new Date(lead.next_action_at) < new Date()) {
      title = `Follow up: ${lead.name}`;
    } else if (!lead.last_interaction_at) {
      title = `First touch: ${lead.name}`;
    } else if (lead.pipeline_stage === 'proposal') {
      title = `Close: ${lead.name}`;
    } else if (dealValue >= 5000) {
      title = `High-value: ${lead.name}`;
    }

    // Generate suggested message
    let suggestedMessage: string | undefined;
    if (recommendedAction === 'whatsapp') {
      if (!lead.last_interaction_at) {
        suggestedMessage = `Hi ${lead.name}, I wanted to reach out about how we can help. Do you have a few minutes?`;
      } else {
        suggestedMessage = `Hi ${lead.name}, following up on our conversation. Any questions?`;
      }
    }

    return {
      id: `action-${lead.id}`,
      leadId: lead.id,
      lead,
      score,
      reasons,
      recommendedAction,
      urgency,
      title,
      suggestedMessage,
      revenueAtRisk,
      dealValue,
    };
  });

  // Sort by score descending, then by revenue at risk
  return scoredLeads
    .sort((a, b) => {
      if (b.score !== a.score) return b.score - a.score;
      return b.revenueAtRisk - a.revenueAtRisk;
    })
    .slice(0, limit);
}
